import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Modal, Space, Tag, Tooltip, Typography, message } from 'antd';
import flowApi, { type CarePhase, type FlowApiKind, type FlowState } from '../../api/flowApi';
import { PHASE_COLORS, PHASE_LABELS, ROLE_LABELS, STEP_STATUS_COLORS, STEP_STATUS_LABELS } from '../../utils/flowLabels';

const { Text } = Typography;

/** Încarcă starea fluxului (faze, pași, tranziții, sarcini) pentru un formular. */
export const useFlowState = (kind: FlowApiKind, formId: string | null | undefined) => {
  const [state, setState] = useState<FlowState | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!formId) return;
    setLoading(true);
    try {
      setState(await flowApi.getState(kind, formId));
    } catch (e) {
      // starea fluxului e auxiliară: wizard-ul rămâne utilizabil dacă endpoint-ul pică
      console.error('Eroare la încărcarea stării fluxului', e);
    } finally {
      setLoading(false);
    }
  }, [kind, formId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { state, loading, reload };
};

interface FlowPanelProps {
  kind: FlowApiKind;
  formId: string;
  state: FlowState | null;
  /** Cheile de pas afișate pe ecranul curent (un index de wizard poate avea mai multe chei). */
  stepKeys: string[];
  /** lesionId pentru pașii per-leziune; undefined pentru pașii la nivel de formular. */
  scopeId?: string;
  /** Pasul e per-leziune și nu există leziune selectată. */
  scopeMissing?: boolean;
  onChanged: () => Promise<void> | void;
}

const FlowPanel = ({ kind, formId, state, stepKeys, scopeId, scopeMissing, onChanged }: FlowPanelProps) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [reopenFor, setReopenFor] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  if (!state) return null;

  const steps = state.steps.filter((s) => stepKeys.includes(s.key));
  const phaseClosed = state.phase === 'CLOSED';

  const run = async (id: string, action: () => Promise<string | void>) => {
    setBusy(id);
    try {
      const msg = await action();
      if (msg) message.success(msg);
      await onChanged();
    } finally {
      // erorile (403/400 cu cod FLOW_*) sunt afișate de interceptorul axios
      setBusy(null);
    }
  };

  const advance = (to: CarePhase) =>
    run(`adv-${to}`, async () => {
      const r = await flowApi.advance(kind, formId, to);
      return `Faza: ${PHASE_LABELS[r.to]}${r.tasksCreated ? ` — ${r.tasksCreated} sarcină(i) create` : ''}`;
    });

  return (
    <Card size="small" style={{ marginBottom: 12 }} data-testid="flow-panel">
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Space wrap align="center">
          <Text strong>Fază clinică:</Text>
          <Tag color={PHASE_COLORS[state.phase]} data-testid="flow-phase">
            {PHASE_LABELS[state.phase]}
          </Tag>
          {phaseClosed && <Text type="secondary">Episodul este închis.</Text>}
        </Space>

        {steps.map((step) => (
          <Space key={step.key} wrap align="center" data-testid={`flow-step-${step.key}`}>
            <Text>{steps.length > 1 ? step.title : 'Pasul curent'}:</Text>
            <Tag color={STEP_STATUS_COLORS[step.status]}>{STEP_STATUS_LABELS[step.status]}</Tag>
            {step.pendingReview && (
              <Tooltip title="Roluri și reguli propuse, în curs de validare medicală">
                <Tag>de validat</Tag>
              </Tooltip>
            )}
            {step.canWrite ? (
              <>
                <Tooltip title={scopeMissing ? 'Selectează mai întâi o leziune' : undefined}>
                  <Button
                    size="small"
                    type="primary"
                    disabled={scopeMissing || phaseClosed}
                    loading={busy === `done-${step.key}`}
                    onClick={() =>
                      run(`done-${step.key}`, async () => {
                        const r = await flowApi.completeStep(kind, formId, step.key, scopeId);
                        return `Pas finalizat${r.tasksCreated ? ` — ${r.tasksCreated} sarcină(i) trimise` : ''}`;
                      })
                    }
                  >
                    Finalizează pasul
                  </Button>
                </Tooltip>
                {step.status === 'COMPLETED' && (
                  <Button size="small" onClick={() => setReopenFor(step.key)}>
                    Redeschide
                  </Button>
                )}
              </>
            ) : (
              <Text type="secondary">Doar citire pentru rolul tău</Text>
            )}
          </Space>
        ))}

        {state.transitions.length > 0 && (
          <Space wrap align="center">
            <Text strong>Mută episodul:</Text>
            {state.transitions.map((t) => (
              <Tooltip
                key={t.id}
                title={t.ready ? t.description : `${t.description} Lipsesc: ${t.missingLabels.join(', ')}.`}
              >
                <span>
                  <Button
                    size="small"
                    disabled={!t.ready}
                    loading={busy === `adv-${t.to}`}
                    onClick={() => void advance(t.to)}
                    data-testid={`flow-advance-${t.to}`}
                  >
                    → {PHASE_LABELS[t.to]}
                  </Button>
                </span>
              </Tooltip>
            ))}
          </Space>
        )}

        {state.openTasks.length > 0 && (
          <Alert
            type="info"
            showIcon
            message="Sarcini deschise"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {state.openTasks.map((t) => (
                  <li key={t.id}>
                    {ROLE_LABELS[t.requiredRole] ?? t.requiredRole}: {t.label}
                  </li>
                ))}
              </ul>
            }
          />
        )}
      </Space>

      <Modal
        open={reopenFor !== null}
        title="Redeschide pasul"
        okText="Redeschide"
        cancelText="Anulează"
        okButtonProps={{ disabled: reason.trim().length < 3 }}
        onCancel={() => {
          setReopenFor(null);
          setReason('');
        }}
        onOk={async () => {
          const key = reopenFor;
          if (!key) return;
          await run(`reopen-${key}`, async () => {
            await flowApi.reopenStep(kind, formId, key, reason.trim(), scopeId);
            return 'Pas redeschis';
          });
          setReopenFor(null);
          setReason('');
        }}
      >
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Motivul redeschiderii (obligatoriu, apare în audit)"
        />
      </Modal>
    </Card>
  );
};

export default FlowPanel;
